import { Facility } from '@reliance/types/models/Facility';
import { TvSource } from '@reliance/types/models/Tv';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import api from '../common/api';
import ConfirmModal from '../common/dialogs/ConfirmModal';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';
import TvSourceTable, { TvSourceTableHandle } from '../tv/TvSourceTable';

type FacilityTvAudioTabProps = {
  facility: Facility;
};

const FacilityTvAudioTab: React.FC<FacilityTvAudioTabProps> = ({ facility }) => {
  const unassignedRef = useRef<TvSourceTableHandle>(null);
  const assignedRef = useRef<TvSourceTableHandle>(null);
  const [unassignedLoading, setUnassignedLoading] = useState(false);
  const [assignedLoading, setAssignedLoading] = useState(false);
  const [assignSource, setAssignSource] = useState<TvSource | null>(null);

  useNotifyTabLoading('tv-audio', unassignedLoading || assignedLoading);

  const unassignedFilters = useMemo(() => ({ facilityId: -1 }), []);
  const assignedFilters = useMemo(() => ({ facilityId: facility.id }), [facility.id]);
  const assignedHiddenColumns = useMemo(() => ['facility'], []);
  const unassignedHiddenColumns = useMemo(
    () => ['facility', 'housingUnits', 'relayControlUrl', 'relayIngestUrl'],
    []
  );

  const refreshBoth = useCallback(() => {
    unassignedRef.current?.refresh();
    assignedRef.current?.refresh();
  }, []);

  const extraRowActions = useCallback(
    (source: TvSource) => (
      <Button
        size="sm"
        variant="outline-primary"
        className="text-nowrap"
        onClick={() => setAssignSource(source)}>
        Assign
      </Button>
    ),
    []
  );

  return (
    <div className="jarviswidget jarviswidget-color-blueDark">
      <div className="content">
        <Card className="mt-3">
          <Card.Header>At this facility</Card.Header>
          <Card.Body>
            <TvSourceTable
              ref={assignedRef}
              filters={assignedFilters}
              hiddenColumns={assignedHiddenColumns}
              onLoadingChange={setAssignedLoading}
              onChanged={refreshBoth}
            />
          </Card.Body>
        </Card>

        <Card className="mt-3">
          <Card.Header>Available to assign</Card.Header>
          <Card.Body>
            <TvSourceTable
              ref={unassignedRef}
              filters={unassignedFilters}
              hiddenColumns={unassignedHiddenColumns}
              extraRowActions={extraRowActions}
              onLoadingChange={setUnassignedLoading}
              onChanged={refreshBoth}
            />
          </Card.Body>
        </Card>

        {assignSource && (
          <ConfirmModal
            show={true}
            title="Assign TV Stream"
            acceptText="Assign"
            onAccept={async () => {
              await api.put(`/tv/source/${assignSource.id}`, { facilityId: facility.id });
              refreshBoth();
            }}
            onClose={() => setAssignSource(null)}>
            Are you sure you want to assign the TV stream
            "{assignSource.name ?? assignSource.macAddress}" to {facility.name ?? `facility ${facility.id}`}?
          </ConfirmModal>
        )}
      </div>
    </div>
  );
};

export default FacilityTvAudioTab;
